import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useT } from '@dia/i18n';
import { ageSeconds, freshnessOf, loadPower, useSyncStatus } from '@dia/db';
import { Button } from '@dia/ui';
import { supabase, sync } from '../supabase';
import { RequireAuth, type Identity } from './lib/identity';
import { Icon } from './lib/icons';
import {
  loadJobCatalog,
  loadOpenRequests,
  loadRuns,
  loadStaysToday,
  loadWeather,
  precacheOps,
  readStoredWeather,
  useLoad,
} from './lib/data';
import { runPhase } from './lib/checklist';
import { islandToday } from './lib/stays';
import { requestText } from './lib/text';
import { formatDay, formatTime, panamaDateOf } from './lib/time';
import { weatherLine, skyFromCode } from './lib/weather';
import { Card, NavRow, Notice, Page, Pill } from './lib/ui';

/**
 * Hoy: who is on the island, today's runs, open requests, one line of weather.
 * Nothing else (Section 7). Three things per screen, and every screen answers
 * "what do I do next".
 *
 * Hoy is the home screen, at `/`, for every role. Everything else is one tap
 * away in the tab bar (Limpieza, Reportar, Sistemas) or the Mas list.
 */
export function Hoy() {
  return <RequireAuth>{(who) => <HoyBody who={who} />}</RequireAuth>;
}

function HoyBody({ who }: { who: Identity }) {
  const { t, lang } = useT();
  const status = useSyncStatus(sync);
  const key = String(status.pending);

  useEffect(() => {
    void precacheOps();
  }, []);

  const stays = useLoad(loadStaysToday, 'stays', 5 * 60_000);
  const runs = useLoad(loadRuns, `runs${key}`, 60_000);
  const catalog = useLoad(loadJobCatalog, 'catalog');
  const requests = useLoad(loadOpenRequests, 'requests', 60_000);
  const weatherLoad = useLoad(loadWeather, 'weather', 10 * 60_000);
  const power = useLoad(() => loadPower(supabase), 'power', 5 * 60_000);

  const today = panamaDateOf();
  const island = islandToday(
    (stays.data ?? []).map((s) => ({ ...s, status: s.status })),
    today,
  );

  const mine = (runs.data ?? []).filter(
    (r) => (r.assigned_to === who.staffId || r.assigned_to === null) && r.review_status !== 'approved',
  );
  const houseName = (id: string | null) => {
    const house = catalog.data?.houses.find((h) => h.id === id);
    return house ? (lang === 'es' ? house.name_es : house.name_en) : t('ops.cleaning.house_wide');
  };
  const listName = (id: string) => {
    const list = catalog.data?.checklists.find((c) => c.id === id);
    return list ? (lang === 'es' ? list.name_es : list.name_en) : '';
  };

  const dash = power.data?.dashboard ?? null;
  const powerAlert =
    dash &&
    dash.status.alert.open &&
    dash.status.alert.openedAt &&
    freshnessOf(ageSeconds(dash.computedAt, Date.now(), power.data?.skewMs ?? 0)) !== 'silent'
      ? { openedAt: dash.status.alert.openedAt }
      : null;

  const openRequests = requests.data ?? [];
  const weather = weatherLoad.data ?? readStoredWeather();
  const wet = weather ? skyFromCode(weather.code).wet : false;

  return (
    <Page title={t('ops.nav.today')}>
      <p className="mb-4 -mt-1 text-inkMuted">
        {t('ops.today.hello', { name: who.name })}
        <span className="text-inkFaint"> · {formatDay(today, lang)}</span>
      </p>

      {/* Only when something is actually stuck. A queue that drains is silent. */}
      {status.dead > 0 && (
        <p role="alert" className="mb-4 rounded border-l-4 border-danger bg-surface p-3">
          <Link to="/sin-enviar" className="text-ink underline underline-offset-4">
            {t('sync.dead_count', { count: status.dead })}
          </Link>
        </p>
      )}

      {/* Only while a low battery alert is open, and only if the island is still
          reporting: an hour-old "alert" is not news. */}
      {powerAlert && (
        <div className="mb-4">
          <Notice tone="danger">
            <span className="block font-semibold">{t('ops.power.alert_title')}</span>
            <span className="block">{t('ops.power.alert_body', { time: formatTime(powerAlert.openedAt, lang) })}</span>
            <Link to="/energia" className="mt-1 inline-block min-h-tap text-ink underline underline-offset-4">
              {t('ops.power.alert_see')}
            </Link>
          </Notice>
        </div>
      )}

      <div className="flex flex-col gap-5">
        <Card>
          <h2 className="mb-2 flex items-center gap-2 text-[1.15em] font-semibold">
            <Icon name="users" /> {t('ops.today.in_house')}
          </h2>
          {island.inHouse.people > 0 ? (
            <>
              <p>
                {island.inHouse.children > 0
                  ? t('ops.today.people_with_children', {
                      people: island.inHouse.people,
                      adults: island.inHouse.adults,
                      children: island.inHouse.children,
                    })
                  : t('ops.today.people_only_adults', { adults: island.inHouse.adults })}
              </p>
              {island.inHouse.departsOn && (
                <p className="text-inkMuted">
                  {t('ops.today.leave_on', { date: formatDay(island.inHouse.departsOn, lang) })}
                </p>
              )}
            </>
          ) : (
            <p className="text-inkMuted">{t('ops.today.nobody')}</p>
          )}
          {/* Silent on a day with no movement, which is most days: an empty
              "Arriving today: 0 / Leaving today: 0" pair was noise the crew
              had to read past every single morning. */}
          {(island.arrivals > 0 || island.departures > 0) && (
            <p className="mt-2 flex flex-wrap gap-2">
              {island.arrivals > 0 && (
                <Pill tone="info">{t('ops.today.arrivals_count', { count: island.arrivals })}</Pill>
              )}
              {island.departures > 0 && (
                <Pill tone="neutral">{t('ops.today.departures_count', { count: island.departures })}</Pill>
              )}
            </p>
          )}
        </Card>

        <Card>
          <h2 className="mb-2 flex items-center gap-2 text-[1.15em] font-semibold">
            <Icon name="broom" /> {t('ops.today.my_jobs')}
          </h2>
          {mine.length === 0 ? (
            <p className="text-inkMuted">{t('ops.today.nothing')}</p>
          ) : (
            <ul className="m-0 flex list-none flex-col gap-2 p-0">
              {mine.slice(0, 4).map((run) => {
                const phase = runPhase(run);
                return (
                  <li key={run.id}>
                    <Link
                      to={`/limpieza/${run.id}`}
                      className="flex min-h-tap flex-col gap-1 rounded border border-line p-3 text-ink no-underline"
                    >
                      <span className="font-semibold">{houseName(run.house_id)}</span>
                      <span className="text-inkMuted">{listName(run.checklist_id)}</span>
                      <span>
                        <Pill tone={phase === 'redo' ? 'danger' : phase === 'waiting' ? 'info' : 'warn'}>
                          {t(`ops.phase.${phase}`)}
                        </Pill>
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
          <Link to="/limpieza" className="mt-3 inline-block">
            <Button block>
              <Icon name="broom" /> {t('ops.today.start_cleaning')}
            </Button>
          </Link>
        </Card>

        <Card>
          <h2 className="mb-2 flex items-center gap-2 text-[1.15em] font-semibold">
            <Icon name="chat" /> {t('ops.today.open_requests')}
          </h2>
          {openRequests.length === 0 ? (
            <p className="text-inkMuted">{t('ops.today.no_requests')}</p>
          ) : (
            <>
              <p className="mb-2">
                {t(
                  openRequests.length === 1 ? 'ops.today.requests_count_one' : 'ops.today.requests_count',
                  { count: openRequests.length },
                )}
              </p>
              <ul className="m-0 flex list-none flex-col gap-2 p-0">
                {openRequests.slice(0, 3).map((r) => (
                  <li key={r.id} className="rounded border border-line p-3">
                    <Pill tone={r.status === 'new' ? 'warn' : 'neutral'}>{t(`ops.request.status.${r.status}`)}</Pill>{' '}
                    <span className="font-medium">{t(`ops.request.type.${r.type}`)}</span>
                    <p className="mt-1">{requestText(r, lang)}</p>
                  </li>
                ))}
              </ul>
            </>
          )}
          <div className="mt-3">
            <NavRow to="/mensajes" icon="chat" title={t('ops.today.see_messages')} />
          </div>
        </Card>

        <Card>
          <h2 className="mb-1 flex items-center gap-2 text-[1.15em] font-semibold">
            <Icon name="sun" /> {t('ops.today.weather')}
          </h2>
          {weather ? (
            <>
              <p>{weatherLine(weather, lang)}</p>
              {wet && <p className="font-medium text-warn">{t('ops.today.wet_warning')}</p>}
              <p className="text-inkMuted">{t('ops.today.weather_at', { time: formatTime(weather.at, lang) })}</p>
            </>
          ) : (
            <p className="text-inkMuted">{t('ops.today.weather_none')}</p>
          )}
        </Card>
      </div>
    </Page>
  );
}
